/** @jsxImportSource @emotion/react */
import React from "react";
import {
    PNCScheduleGrid, PNCScheduleItem, PNCScheduleVisitNum,
    PNCScheduleLabel, PNCScheduleDate, PNCScheduleStatus,
} from "./PostnatalCareStyles";

/* ═══════════════════════════════════════════════════════════════
   PNCScheduleTracker — Visual 4-visit PNC schedule tracker
   Shows each PNC contact with status: attended, due, missed,
   upcoming, future. Clicking attended visits opens details.
   ═══════════════════════════════════════════════════════════════ */

const statusLabels = {
    attended: "Attended",
    due: "Due Now",
    missed: "Missed",
    upcoming: "Upcoming",
    future: "Scheduled",
};

export const PNCScheduleTracker = function (props) {
    var schedule = props.schedule || [];
    var onViewVisit = props.onViewVisit;
    var deliveryDate = props.deliveryDate;

    var attended = schedule.filter(function (s) { return s.status === "attended"; }).length;

    return (
        <div>
            {/* ─── Header ─── */}
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "16px",
            }}>
                <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#333" }}>
                    PNC Visit Schedule
                </h3>
                <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#555" }}>
                    {attended}/4 visits completed
                </span>
            </div>

            {/* ─── Delivery date reference ─── */}
            {deliveryDate && (
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "16px",
                    padding: "8px 14px",
                    borderRadius: "8px",
                    background: "#f5f5f5",
                    fontSize: "0.8rem",
                    color: "#666",
                }}>
                    <span>Delivery Date: <strong style={{ color: "#333" }}>{deliveryDate}</strong></span>
                </div>
            )}

            {/* ─── Progress Bar ─── */}
            <div style={{
                height: "6px",
                borderRadius: "3px",
                background: "#f0f0f0",
                marginBottom: "16px",
                overflow: "hidden",
            }}>
                <div style={{
                    height: "100%",
                    borderRadius: "3px",
                    background: attended === 4 ? "#2e7d32" : "#00695c",
                    width: (attended / 4 * 100) + "%",
                    transition: "width 0.5s ease",
                }} />
            </div>

            {/* ─── Schedule Grid ─── */}
            <PNCScheduleGrid>
                {schedule.map(function (item) {
                    return (
                        <PNCScheduleItem
                            key={item.number}
                            status={item.status}
                            onClick={function () {
                                if (item.status === "attended" && item.visit && onViewVisit) {
                                    onViewVisit(item.visit);
                                }
                            }}
                        >
                            <PNCScheduleVisitNum status={item.status}>
                                {item.status === "attended" ? "✓" : item.number}
                            </PNCScheduleVisitNum>
                            <PNCScheduleLabel>{item.label}</PNCScheduleLabel>
                            <PNCScheduleDate>
                                {item.timing}
                            </PNCScheduleDate>
                            <PNCScheduleDate style={{ marginTop: "2px", fontWeight: 600 }}>
                                {item.scheduled_date || "—"}
                            </PNCScheduleDate>
                            {item.status === "attended" && item.visit && (
                                <PNCScheduleDate style={{ marginTop: "4px", color: "#2e7d32" }}>
                                    Done: {item.visit.visit_date}
                                </PNCScheduleDate>
                            )}
                            <PNCScheduleStatus status={item.status}>
                                {statusLabels[item.status] || item.status}
                            </PNCScheduleStatus>
                        </PNCScheduleItem>
                    );
                })}
            </PNCScheduleGrid>

            {/* ─── Legend ─── */}
            <div style={{
                display: "flex",
                gap: "16px",
                marginTop: "16px",
                padding: "10px 14px",
                borderRadius: "8px",
                background: "#fafafa",
                fontSize: "0.72rem",
                flexWrap: "wrap",
            }}>
                {[
                    { status: "attended", color: "#2e7d32", label: "Attended" },
                    { status: "due", color: "#f57f17", label: "Due Now" },
                    { status: "missed", color: "#c62828", label: "Missed" },
                    { status: "upcoming", color: "#607d8b", label: "Upcoming" },
                ].map(function (item) {
                    return (
                        <div key={item.status} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <div style={{
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                                background: item.color,
                            }} />
                            <span style={{ color: "#666" }}>{item.label}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
